import React, { useCallback, useEffect, useState } from 'react';
import { monitoringAPI } from '../../services/api';

// Per-URL status from the monitoring v2 engine: up / warning / degraded / down,
// current incident, uptime windows, timing breakdown, SSL expiry and which
// worker locations checked it. Self-contained (fetches its own data).

interface Timings { dns_ms: number | null; connect_ms: number | null; tls_ms: number | null; ttfb_ms: number | null; total_ms: number | null }
interface UrlHealth {
  url: string;
  status: 'up' | 'warning' | 'degraded' | 'down' | 'unknown';
  status_since: string | null;
  status_code: number | null;
  response_time: number | null;
  last_checked: string | null;
  error_message: string | null;
  warning_message: string | null;
  timings: Timings | null;
  ssl_expiry_days: number | null;
  redirect_count: number;
  interval_seconds: number | null;
  uptime: { h24: number | null; d7: number | null; d30: number | null };
  incident: { id: number; started_at: string; duration_seconds: number; error_message: string | null; confirmed_locations: string | null; failed_checks: number } | null;
  locations: { worker_id: number; location: string; result: string; status_code: number | null; response_time: number | null; checked_at: string }[];
}

const STATUS: Record<string, { label: string; cls: string; icon: string }> = {
  up: { label: 'Up', cls: 'success', icon: 'bi-check-circle-fill' },
  warning: { label: 'Up (warning)', cls: 'warning', icon: 'bi-exclamation-circle-fill' },
  degraded: { label: 'Degraded', cls: 'warning', icon: 'bi-exclamation-triangle-fill' },
  down: { label: 'Down', cls: 'danger', icon: 'bi-x-octagon-fill' },
  unknown: { label: 'Pending first check', cls: 'secondary', icon: 'bi-hourglass-split' },
};

export const formatDurationSecs = (secs: number | null | undefined): string => {
  const s = Math.max(0, Math.round(secs || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const pct = (v: number | null) => (v == null ? '—' : `${v.toFixed(v === 100 ? 0 : 2)}%`);
const pctCls = (v: number | null) => (v == null ? 'text-muted' : v >= 99.9 ? 'text-success' : v >= 99 ? 'text-warning' : 'text-danger');

const TimingBar: React.FC<{ t: Timings }> = ({ t }) => {
  const parts = [
    { k: 'DNS', v: t.dns_ms || 0, c: '#6f42c1' },
    { k: 'Connect', v: t.connect_ms || 0, c: '#0d6efd' },
    { k: 'TLS', v: t.tls_ms || 0, c: '#20c997' },
    { k: 'Server (TTFB)', v: Math.max(0, (t.ttfb_ms || 0) - (t.dns_ms || 0) - (t.connect_ms || 0) - (t.tls_ms || 0)), c: '#fd7e14' },
    { k: 'Download', v: Math.max(0, (t.total_ms || 0) - (t.ttfb_ms || 0)), c: '#adb5bd' },
  ];
  const total = Math.max(1, parts.reduce((a, p) => a + p.v, 0));
  return (
    <div>
      <div className="d-flex rounded overflow-hidden" style={{ height: 8 }} aria-label="Timing breakdown">
        {parts.map((p) => p.v > 0 && <div key={p.k} style={{ width: `${(p.v / total) * 100}%`, background: p.c }} title={`${p.k}: ${p.v} ms`} />)}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-1">
        {parts.map((p) => (
          <small key={p.k} className="text-muted">
            <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: p.c }} />
            {p.k} {p.v} ms
          </small>
        ))}
      </div>
    </div>
  );
};

interface IncidentRow {
  id: number; url: string; started_at: string; ended_at: string | null; duration_seconds: number;
  is_ongoing: boolean; status: string; ended_reason: string | null; error_message: string | null;
  confirmed_locations: string | null; total_failed_checks: number;
}

const UrlStatusPanel: React.FC<{ assetId: number }> = ({ assetId }) => {
  const [urls, setUrls] = useState<UrlHealth[]>([]);
  const [incidents, setIncidents] = useState<IncidentRow[]>([]);
  const [workersOnline, setWorkersOnline] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await monitoringAPI.getHealthStatus(assetId);
      setUrls(res.data.data.urls || []);
      setWorkersOnline(res.data.data.workers_online ?? null);
      const inc = await monitoringAPI.getDowntimeIncidents(assetId, 30).catch(() => null);
      if (inc) setIncidents(inc.data.data.incidents || []);
    } catch {
      /* page shows the rest of the data; keep last state */
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    const clock = setInterval(() => setTick((x) => x + 1), 1000); // live incident durations
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load]);

  if (loading) return <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" /></div>;
  if (urls.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0"><i className="bi bi-globe me-2"></i>Monitored URLs</h6>
        {workersOnline === 0 && <small className="text-danger"><i className="bi bi-exclamation-triangle me-1"></i>No monitoring worker online — checks are paused</small>}
      </div>
      {urls.map((u) => {
        const st = STATUS[u.status] || STATUS.unknown;
        const liveSecs = u.incident ? Math.floor((Date.now() - new Date(u.incident.started_at).getTime()) / 1000) : 0;
        return (
          <div key={u.url} className={`card border-0 shadow-sm mb-3 border-start border-4 border-${st.cls}`}>
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div className="text-break">
                  <span className={`badge bg-${st.cls} me-2`}><i className={`bi ${st.icon} me-1`}></i>{st.label}</span>
                  <a href={u.url} target="_blank" rel="noopener noreferrer" className="fw-semibold text-decoration-none">{u.url}</a>
                  <div className="small text-muted mt-1">
                    {u.status_code ? `HTTP ${u.status_code}` : ''}
                    {u.response_time != null ? ` · ${u.response_time} ms` : ''}
                    {u.last_checked ? ` · checked ${new Date(u.last_checked).toLocaleTimeString()}` : ''}
                    {u.interval_seconds ? ` · every ${Math.round(u.interval_seconds / 60) || 1} min` : ''}
                    {u.redirect_count > 0 ? ` · ${u.redirect_count} redirect${u.redirect_count > 1 ? 's' : ''}` : ''}
                  </div>
                </div>
                <div className="d-flex gap-3 text-center">
                  {(['h24', 'd7', 'd30'] as const).map((k) => (
                    <div key={k}>
                      <div className={`fw-bold ${pctCls(u.uptime[k])}`}>{pct(u.uptime[k])}</div>
                      <small className="text-muted">{k === 'h24' ? '24h' : k === 'd7' ? '7 days' : '30 days'}</small>
                    </div>
                  ))}
                  <div>
                    <div className={`fw-bold ${u.ssl_expiry_days == null ? 'text-muted' : u.ssl_expiry_days < 7 ? 'text-danger' : u.ssl_expiry_days < 21 ? 'text-warning' : 'text-success'}`}>
                      {u.ssl_expiry_days == null ? '—' : `${u.ssl_expiry_days}d`}
                    </div>
                    <small className="text-muted">SSL expiry</small>
                  </div>
                </div>
              </div>

              {u.incident && (
                <div className="alert alert-danger py-2 mt-3 mb-0">
                  <i className="bi bi-exclamation-octagon me-1"></i>
                  <strong>Down since {new Date(u.incident.started_at).toLocaleString()}</strong> — {formatDurationSecs(liveSecs)}
                  {u.incident.error_message && <> · {u.incident.error_message}</>}
                  {u.incident.confirmed_locations && <div className="small">Confirmed from: {u.incident.confirmed_locations}</div>}
                </div>
              )}
              {!u.incident && u.status === 'degraded' && (
                <div className="alert alert-warning py-2 mt-3 mb-0 small">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  A check failed{u.error_message ? ` (${u.error_message})` : ''}. Re-checking from {workersOnline && workersOnline > 1 ? 'another location' : 'the monitor'} before declaring an outage.
                </div>
              )}
              {u.warning_message && u.status !== 'down' && (
                <div className="small text-warning mt-2"><i className="bi bi-info-circle me-1"></i>{u.warning_message}</div>
              )}

              {u.timings && u.status !== 'down' && <div className="mt-3"><TimingBar t={u.timings} /></div>}

              {u.locations.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {u.locations.map((l) => (
                    <span key={l.worker_id} className={`badge rounded-pill border text-${l.result === 'down' ? 'danger' : 'body'} bg-light`}>
                      <i className={`bi bi-geo-alt-fill me-1 text-${l.result === 'down' ? 'danger' : l.result === 'up_with_warning' ? 'warning' : 'success'}`}></i>
                      {l.location}{l.response_time != null && l.result !== 'down' ? ` · ${l.response_time} ms` : l.status_code ? ` · HTTP ${l.status_code}` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white"><h6 className="mb-0"><i className="bi bi-clock-history me-2"></i>Incident history (30 days)</h6></div>
        <div className="card-body p-0">
          {incidents.length === 0 ? (
            <div className="text-center text-muted py-4 small"><i className="bi bi-check-circle text-success me-1"></i>No confirmed outages in the last 30 days</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr><th>URL</th><th>Started</th><th>Ended</th><th>Duration</th><th>Status</th><th>Cause</th></tr>
                </thead>
                <tbody>
                  {incidents.slice(0, 25).map((i) => (
                    <tr key={i.id}>
                      <td className="text-break small" style={{ maxWidth: 260 }}>{i.url}</td>
                      <td className="small text-nowrap">{new Date(i.started_at).toLocaleString()}</td>
                      <td className="small text-nowrap">{i.ended_at ? new Date(i.ended_at).toLocaleString() : '—'}</td>
                      <td className="small fw-semibold">{formatDurationSecs(i.is_ongoing ? (Date.now() - new Date(i.started_at).getTime()) / 1000 : i.duration_seconds)}</td>
                      <td>
                        {i.is_ongoing ? <span className="badge bg-danger">Ongoing</span>
                          : i.status === 'cancelled' ? <span className="badge bg-secondary" title={i.ended_reason || ''}>Closed ({(i.ended_reason || '').replace('_', ' ')})</span>
                          : <span className="badge bg-success">Resolved</span>}
                      </td>
                      <td className="small text-muted text-break" style={{ maxWidth: 260 }}>
                        {i.error_message || '—'}{i.confirmed_locations ? ` · ${i.confirmed_locations}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UrlStatusPanel;
